import classNames from "classnames";
import { observer } from "mobx-react";
import React, { useState } from "react";

import { Field, RecordVersion, TableName } from "@/api";
import { titleCase } from "@/components/filters/helpers";
import MenuActions from "@/components/table/menu-actions/MenuActions";
import AddNewRow from "@/components/table/shared/AddNewRow";
import { customColumns } from "@/components/table/shared/custom-columns/CustomColumns";
import ModuleRecordVersion from "@/components/table/shared/ModuleRecordVersion";
import ModuleTableColumn from "@/components/table/shared/ModuleTableColumn";
import ReorderableTable from "@/components/table/shared/ReorderableTable";
import SectionPopupSettings from "@/components/table/shared/SectionPopupSettings";
import SectionTag from "@/components/table/shared/SectionTag";
import Sharing from "@/components/table/shared/Sharing";
import TableMenu from "@/components/table/shared/tableMenu";
import { useMainStore } from "@/contexts/Store";
import { TopLevelModule } from "@/stores/types/module-workspaces-types";
import { Section } from "@/stores/types/section-tags";

import { useSortingAndPagination } from "../../../hooks/useSortingAndPagination";
import arrowUp from "../../../images/table-image/icon/arrow-up.svg";
import { isLockedRecord } from "../../../stores/helpers/RecordVersionHelper";

type Props = {
  fields?: Field[];
  isDropdownOpen?: boolean;
  moduleWorkspaceID?: number;
  recordVersions?: RecordVersion[];
  sectionTag?: Section;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setShowCheckbox?: (...args: any[]) => any;
  showCheckbox?: boolean;
  tableName?: TableName;
  isDraggable?: boolean;
};

function KeyRiskIndicatorsSection({
  fields,
  moduleWorkspaceID,
  recordVersions,
  sectionTag,
  tableName,
  showCheckbox,
  setShowCheckbox,
  isDraggable,
  ...props
}: Props) {
  // Import MobX stores
  const mainStore = useMainStore();

  // States
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(props.isDropdownOpen);
  const [selectMode, setSelectMode] = useState(false);
  const sectionTableID = `section-table-${sectionTag?.id || 0}`;

  const { createColumn, renameColumn, deleteColumn, AddColumnButton } =
    customColumns({
      sectionTableID,
    });

  // hooks
  const {
    sortByFieldName,
    sortByDirection,
    setSortByFieldName,
    setSortByDirection,
  } = useSortingAndPagination({
    store: mainStore.keyRiskIndicators,
    sectionTagId: sectionTag?.id,
  });

  // Variables
  const { activeWorkspace, isAdmin } = mainStore.context;
  const { canManageSections } = mainStore.userPermissions;

  // Functions
  const dropdownClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const deleteSectionTag = () => {
    mainStore.sectionTags.delete(moduleWorkspaceID, sectionTag?.id);
    mainStore.toast.setText("Section has been deleted!");
  };

  const renameClick = () => {
    setIsEditMode(true);
  };

  const performValidations = ({
    recordVersionID,
    fieldName,
    value,
  }: {
    recordVersionID: number | undefined;
    fieldName: string;
    value: string;
  }): string | undefined => {
    const recordVersion = mainStore.recordVersions.list.find(
      (rv) => rv.id === recordVersionID,
    );

    const field = mainStore.fields.list.find((f) => f.name == fieldName);
    if (field?.validation_rules?.greater_than_or_equal_to) {
      const otherFieldName =
        field.validation_rules.greater_than_or_equal_to.options
          .other_field_name;
      if (!recordVersion) {
        return undefined;
      }

      const otherValue = recordVersion.data[otherFieldName]?.value;

      if (!otherValue) {
        return;
      }
      if (otherValue > parseFloat(value)) {
        return `Error: ${titleCase(fieldName)} must be greater than ${titleCase(
          otherFieldName,
        )}`;
      }
    } else if (field?.validation_rules?.less_than_or_equal_to) {
      const otherFieldName =
        field?.validation_rules.less_than_or_equal_to.options.other_field_name;
      if (!otherFieldName || !recordVersion) {
        return undefined;
      }

      const otherValue = recordVersion.data[otherFieldName]?.value;
      if (!otherValue) {
        return;
      }
      if (otherValue < parseFloat(value)) {
        return `Error: ${titleCase(fieldName)} must be less than ${titleCase(
          otherFieldName,
        )}`;
      }
    }

    return undefined;
  };
  async function createNewRecord() {
    if (tableName) {
      await mainStore.keyRiskIndicators.create({
        sectionTagID: sectionTag?.id,
      });
      mainStore.keyRiskIndicators.index({
        workspaceID: activeWorkspace?.id,
      });
    }
  }
  const resetMenuActions = () => {
    // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
    setShowCheckbox(false);
    setSelectMode(false);
    mainStore.dynamicTable.clearSelectedRecordVersionIDs();
  };

  // @ts-expect-error TS(7006) FIXME: Parameter 'recordVersion' implicitly has an 'any' ... Remove this comment to see the full error message
  const renderRow = (recordVersion, idx) => {
    return (
      <ModuleRecordVersion
        key={recordVersion.id}
        fields={fields}
        recordVersion={recordVersion}
        moduleWorkspaceID={moduleWorkspaceID}
        tableID={recordVersion.table_id}
        tableName={recordVersion.table_name}
        currentTableName={tableName}
        moduleIdentifier={TopLevelModule.KEY_RISK_INDICATORS}
        showCheckboxFromSlideMenu={showCheckbox}
        performValidations={performValidations}
        // persistOnCallback
        inSelectMenuActionMode={selectMode}
        isSorted={!!sortByFieldName}
        order={idx}
        // @ts-expect-error TS(2322) FIXME: Type 'boolean | null' is not assignable to type 'b... Remove this comment to see the full error message
        isLockedTable={isLockedRecord(recordVersion, "key_risk_indicators")}
        isDraggable={isDraggable}
      />
    );
  };

  // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
  const renderColumns = fields.map((field) => {
    return (
      <ModuleTableColumn
        key={field.name}
        field={field}
        // @ts-expect-error TS(2322) FIXME: Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
        sortDirection={field.name === sortByFieldName ? sortByDirection : ""}
        setSortByDirection={setSortByDirection}
        setSortByFieldName={setSortByFieldName}
        onDelete={deleteColumn}
        onRename={renameColumn}
      />
    );
  });

  return (
    <>
      {sectionTag && (
        <div className="dropdown-title-wrap" data-testid="dropdown-section">
          <div
            className={classNames("dropdown-title", {
              "dropdown-open": isDropdownOpen,
            })}
          >
            <div className="dropdown-title-block">
              <div className="dropdown-title-click">
                <div className="dropdown-arrow" onClick={dropdownClick}>
                  <img src={arrowUp} alt="dropdown-arrow" />
                </div>

                {sectionTag.title && (
                  <SectionTag
                    isEditMode={isEditMode}
                    setIsEditMode={setIsEditMode}
                    sectionTagID={sectionTag?.id}
                    sectionTagTitle={sectionTag?.title}
                  />
                )}
              </div>

              {sectionTag.editable && (
                <div className="dropdown-public-block">
                  {isAdmin && (
                    <Sharing
                      moduleWorkspaceID={moduleWorkspaceID}
                      objectID={sectionTag.id}
                      selectedDepartmentIDs={sectionTag.department_ids}
                      selectedUserIDs={sectionTag.user_ids}
                      tableName={tableName}
                      allowSharing
                    />
                  )}

                  {canManageSections && (
                    <SectionPopupSettings
                      onRename={renameClick}
                      onDelete={deleteSectionTag}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isDropdownOpen && (
        <div
          className="dropdown-table"
          id={sectionTableID}
          data-testid="dropdown-table"
        >
          <div className="list-title-table">
            <ul>
              <div className="list-column-wrap">
                <div className="procedures-list-column-first-blocks table-column-reorder">
                  {/* @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'. */}
                  {fields.length > 0 && (
                    <TableMenu
                      setShowCheckbox={setShowCheckbox}
                      resetMenuActions={resetMenuActions}
                      selectMode={selectMode}
                      setSelectMode={setSelectMode}
                    />
                  )}
                </div>
                {showCheckbox && selectMode ? (
                  <div>
                    <MenuActions resetMenuActions={resetMenuActions} />
                  </div>
                ) : (
                  <div className="list-title-table-wrap">
                    {showCheckbox && <div className="checkbox-title-table" />}
                    {renderColumns}
                    <span className="stretch-cell" />
                  </div>
                )}
              </div>
              <AddColumnButton onAdd={createColumn} />
            </ul>
          </div>
          <AddNewRow
            dataTestIdPrefix="add-new"
            recordTypeName={"Key Risk Indicator"}
            onClick={createNewRecord}
            className="add-new-report"
            offset={128}
          />

          <ReorderableTable
            recordVersions={recordVersions}
            renderer={renderRow}
            sectionTag={sectionTag}
            isSorted={!!sortByFieldName}
          />
        </div>
      )}
    </>
  );
}

KeyRiskIndicatorsSection.defaultProps = {
  setShowCheckbox: () => {},
  showCheckbox: false,
};

export default observer(KeyRiskIndicatorsSection);
